import React from 'react';
import { useNavigation } from '../navigation';

interface NavblockProps {
  onViewSavedPurchases?: () => void;
  heading?: string;
  subtitle?: string;
  rightButtonText?: string;
  rightButtonIcon?: React.ReactNode;
  onRightButtonClick?: () => void;
  showRightButton?: boolean;
  isSavedPurchasesVisible?: boolean;
  onNavigateHomeRequest?: () => void;
}

const Navblock: React.FC<NavblockProps> = ({ 
  onViewSavedPurchases,
  heading = 'Purchase',
  subtitle = 'Enter or save your purchased medications here',
  rightButtonText,
  rightButtonIcon,
  onRightButtonClick,
  showRightButton = true,
  isSavedPurchasesVisible = false,
  onNavigateHomeRequest
}) => {
  const { navigateTo } = useNavigation();

  const handleViewSavedPurchases = () => {
    if (onViewSavedPurchases) {
      onViewSavedPurchases();
    }
  };

  const handleRightButtonClick = () => {
    if (onRightButtonClick) {
      onRightButtonClick();
    } else {
      handleViewSavedPurchases();
    }
  };

  // Determine button text based on visibility state
  const getButtonText = () => {
    if (rightButtonText) {
      return rightButtonText;
    }
    return isSavedPurchasesVisible ? 'Hide Saved Purchases' : 'View Saved Purchases';
  };

  // Determine button icon based on visibility state
  const getButtonIcon = () => {
    if (rightButtonIcon) {
      return rightButtonIcon;
    }
    if (isSavedPurchasesVisible) {
      return (
        <svg width={window.innerWidth < 768 ? "16" : "18"} height={window.innerWidth < 768 ? "16" : "18"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      );
    }
    return (
      <svg width={window.innerWidth < 768 ? "16" : "18"} height={window.innerWidth < 768 ? "16" : "18"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    );
  };

  const isBilling = heading === 'Billing';
  const borderColor = isBilling ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)';
  const buttonBorderColor = isBilling ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)';
  const buttonHoverColor = isBilling ? '#ef4444' : '#10b981';
  const buttonHoverBg = isBilling ? '#fef2f2' : '#f0fdf4';
  const buttonHoverShadow = isBilling ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)';

  const isMobile = window.innerWidth < 768;
  const verticalPadding = isMobile ? '1rem' : '1.75rem';
  const horizontalPadding = isMobile ? '1rem' : '2rem';

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
      backdropFilter: 'blur(25px)',
      borderBottom: `1px solid ${borderColor}`,
      padding: `${verticalPadding} ${horizontalPadding}`,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      {/* Back Button - Left */}
      <button
        onClick={() => {
          if (onNavigateHomeRequest) {
            onNavigateHomeRequest();
          } else {
            navigateTo('pharma-homepage');
          }
        }}
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          color: '#6b7280',
          border: `1px solid ${buttonBorderColor}`,
          padding: window.innerWidth < 768 ? '0.75rem 1rem' : '0.875rem 1.25rem',
          borderRadius: '12px',
          fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: window.innerWidth < 768 ? '0.5rem' : '0.625rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0
        }}
        onMouseEnter={(e) => {
          if (e.currentTarget) {
            e.currentTarget.style.borderColor = buttonHoverColor;
            e.currentTarget.style.color = buttonHoverColor;
            e.currentTarget.style.backgroundColor = buttonHoverBg;
            e.currentTarget.style.boxShadow = `0 8px 24px ${buttonHoverShadow}`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          if (e.currentTarget) {
            e.currentTarget.style.borderColor = buttonBorderColor;
            e.currentTarget.style.color = '#6b7280';
            e.currentTarget.style.backgroundColor = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <svg width={window.innerWidth < 768 ? "16" : "18"} height={window.innerWidth < 768 ? "16" : "18"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16,17 21,12 16,7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        {window.innerWidth < 480 ? 'Back' : 'Back to Home'}
      </button>

      {/* Heading - Center */}
      <div style={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        display: window.innerWidth < 768 ? 'none' : 'block'
      }}>
        <h1 style={{
          fontSize: window.innerWidth < 768 ? '1.25rem' : '1.75rem',
          fontWeight: '800',
          background: heading === 'Billing' 
            ? 'linear-gradient(135deg, #1f2937 0%, #ef4444 100%)'
            : 'linear-gradient(135deg, #1f2937 0%, #10b981 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          margin: '0 0 0.25rem 0',
          letterSpacing: '-0.03em'
        }}>
          {heading}
        </h1>
        <p style={{
          fontSize: window.innerWidth < 768 ? '0.75rem' : '0.85rem',
          color: '#6b7280',
          margin: 0,
          fontWeight: '500',
          letterSpacing: '0.01em'
        }}>
          {subtitle}
        </p>
      </div>

      {/* Right Button - Optional */}
      {showRightButton && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          flexShrink: 0 
        }}>
          <button
            onClick={handleRightButtonClick}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              padding: window.innerWidth < 768 ? '0.75rem 1rem' : '0.875rem 1.25rem',
              borderRadius: '10px',
              fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.35)';
                e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
              }
            }}
          >
            {getButtonIcon()}
            <span style={{ display: window.innerWidth < 480 ? 'none' : 'inline' }}>
              {getButtonText()}
            </span>
          </button>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Navblock;

